import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  Backdrop,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import { ChevronRight, ExpandMore, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { AuthContext } from '../components/util/AuthContext';
import AuthorSelector from '../components/AuthorSelector';
import { useQueryParam } from '../components/util/useQueryParam';
import { useContext } from 'react';

import DraggableDialog from '../components/DraggableDialog'; // 드래그 가능한 다이얼로그 컴포넌트
import EstimateWriteDialog from '../components/EstimateWriteDialog'; // 견적서 작성 모달 컴포넌트

const EstimatePage = () => {
  // 고객사 목록을 저장하는 상태
  const [clientCompanies, setClientCompanies] = useState([]);
  // 현재 펼쳐진 고객사의 ID를 저장하는 상태
  const [expandedId, setExpandedId] = useState(null);
  // 각 고객사별 견적서 목록을 저장하는 상태 (key: 고객사 ID, value: 견적서 목록)
  const [estimates, setEstimates] = useState({});
  // 검색어 상태
  const [searchTerm, setSearchTerm] = useState('');
  // 다이얼로그 열림 상태
  const [dialogOpen, setDialogOpen]= useState(false);
  // 선택된 견적서
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  // 수정 모달 상태
  const [writeDialogOpen, setWriteDialogOpen] = useState(false);
  // 로딩 백드롭 모달 상태
  const [isSaving, setIsSaving] = useState(false);
  // 고객사 목록 로딩 상태
  const [isLoading, setIsLoading] = useState(true);
  // 작성자 선택 상태
  const [author, setAuthor] = useState(null);
  const [authorParam, setAuthorParam] = useQueryParam('author', null);
  const authorUsername = authorParam ?? null;

  const { user } = useContext(AuthContext); // 유저 정보를 담고있는 AuthContext

  // 고객사 목록 조회 비동기 함수
  const fetchClientCompanies = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if(authorUsername) params.set('author', authorUsername);
      const response = await axios.get('/api/estimates/by-client', { params });
      setClientCompanies(response.data);
    } catch (error) {
      console.error('고객사 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 컴포넌트 마운트 시 & 작성자 필터 변경 시 고객사 목록을 조회
  useEffect(() => {
    fetchClientCompanies();
  }, [authorUsername]);


  // 특정 고객사의 견적서 목록 조회
  const handleExpandClick = async (id) => {
    // 이미 펼쳐진 항목을 다시 클릭한 경우 접기
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    try {
      setIsSaving(true);
      const params = new URLSearchParams();
      if(authorUsername) params.set('author', authorUsername);
      const response = await axios.get(`/api/estimates/by-client/${id}${params.toString() ? `?${params.toString()}` : ''}`);
      // 견적서 목록 상태 업데이트 (기존 데이터 유지하면서 새 데이터 추가)
      setEstimates(prev => ({
        ...prev,
        [id]: response.data
      }));
      // 펼쳐진 항목 ID 업데이트
      setExpandedId(id);
    } catch (error) {
      console.error('견적서 목록 조회 실패:', error);
      // 선택된 고객사의 견적서가 없습니다 알림
      alert('선택된 고객사의 견적서가 없습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  // 견적서 상세정보 클릭 시 해당 견적 정보를 저장하고 모달을 연다.
  const handleEstimateClick = (estimate) => {
    setSelectedEstimate(estimate);
    setDialogOpen(true);
  };



  // 견적서 작성 버튼 클릭
  const handleNewEstimateClick = () => {
    setWriteDialogOpen(true);
  };

  // 작성 모달 닫힘 콜백
  const handleWriteCloseFromList = (savedDto) => {
    setWriteDialogOpen(false);
    if (savedDto) {
      // 저장 후 바로 미리보기 모달 열기
      setSelectedEstimate(savedDto);
      setDialogOpen(true);
      
      // 고객사 목록 조회
      fetchClientCompanies();

    }
  };

  // 저장 직후 호출될 콜백
  const handleEstimateSave = async (updated) => {

    // 현재 열려 있는 고객사 그룹 ID 가져오기
    const clientId = expandedId;
  
    // 고객사 목록 조회
    fetchClientCompanies();

    if (clientId != null) {
      try {
        // /api/estimates/by-client/{clientId} 를 다시 조회
        const p = new URLSearchParams();
        if (authorUsername) p.set('author', authorUsername);
        const resp = await axios.get(`/api/estimates/by-client/${clientId}${p.toString()?`?${p.toString()}`:''}`);
        // 해당 그룹의 리스트를 최신 데이터로 덮어쓰기
        setEstimates(prev => ({
          ...prev,
          [clientId]: resp.data
        }));
      } catch (err) {
        console.error('견적서 목록 재조회 실패', err);
      }
    }

    // 선택 견적을 갱신하고, 미리보기 모달 열기
    setSelectedEstimate(updated);
    setDialogOpen(true);
  };

  // 견적서 삭제
  const handleEstimateDelete = async (id) => {
    // 삭제 확인 알림
    const confirm = window.confirm('정말 삭제하시겠습니까?');
    if (!confirm) return;

    try {
      setIsSaving(true);
      await axios.delete(`/api/estimates/${id}`);
      // 삭제 후 견적서 목록 재조회
      try{
        const p2 = new URLSearchParams();
        if (authorUsername) p2.set('author', authorUsername);
        const resp = await axios.get(`/api/estimates/by-client/${expandedId}${p2.toString()?`?${p2.toString()}`:''}`);
        setEstimates(prev => ({
          ...prev,
          [expandedId]: resp.data
        }));
      } catch (err) {
        // 견적서 재조회 실패시 알람 후 고객사 목록 재조회
        console.error('견적서 목록 재조회 실패', err);
        alert('선택된 고객사의 견적서가 없습니다.');
        fetchClientCompanies();
      }
    } catch (error) {
      console.error('견적서 삭제 실패:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // 내가 작성한 견적서만 보기
  const handleMyEstimatesToggle = (e) => {
    const on = e.target.checked;
    if(on){
      setAuthor({ username: user?.username, userNick: user?.userNick });
      setAuthorParam(user?.username ?? null);
    }
    else{
      setAuthor(null);
      setAuthorParam(null);
    }
  };


  // 검색어에 따른 고객사 목록 필터링
  const filteredCompanies = clientCompanies.filter(company =>
    company.clientCompany.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{backgroundColor: 'white', borderRadius: '5px'}}>

    {/* 로딩 백드롭, 모달보다 Index값이 1 높아야 함 */}
    <Backdrop open={isSaving} sx={{ zIndex: theme => theme.zIndex.modal + 1, color: '#fff'}}>
      <CircularProgress color="inherit"/>
    </Backdrop>

    <Box sx={{margin: '0 auto'}}>
      {/* 카드형 컨테이너 */}
      <Paper elevation={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', padding: '10px 10px 0px 10px' }}>
          <Button
            color="primary"
            startIcon={<EditIcon/>}
            onClick={handleNewEstimateClick}
          >
            견적서 작성
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2}}>
            <TextField
              variant="standard"
              placeholder="고객사 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ style : { fontSize: '0.875rem'}}}
            />
            {/* 작성자 선택 (URL ?author= 동기화 + 새로고침 복원) */}
            <AuthorSelector value={author} onChange={setAuthor} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!authorUsername && authorUsername === user?.username}
                  onChange={handleMyEstimatesToggle}
                />
              }
              label="내 글만"
            />
          </Box>
        </Box>
        
        {/* 고객사 목록 */}
        <List>
          {isLoading ? (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          ) : filteredCompanies.length === 0 ? (
            <Box sx={{ width: '100%', textAlign: 'center', padding: '1rem' }}>
              <Typography variant="body1">검색 결과가 없습니다.</Typography>
            </Box>
          ) : (
            filteredCompanies.map((company, index) => (
              <React.Fragment key={company.id}>
                {/* 구분선 (첫 항목 제외) */}
                {index > 0 && <Divider/>}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleExpandClick(company.id)}>
                    <ListItemText primary={company.clientCompany}/>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end" 
                        onClick={(e) => {
                          e.stopPropagation(); // 버튼 클릭 시 상위 요소 클릭 이벤트 방지
                          handleExpandClick(company.id);
                        }}
                      >
                        {/* 현재 상태에 따른 아이콘 표시 */}
                        {expandedId === company.id ? <ExpandMore /> : <ChevronRight/>}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItemButton>
                </ListItem>

                <Collapse in={expandedId === company.id} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {/* 견적서 항목들 */}
                    {(() => {
                      const list = estimates[company.id] || [];
                      // 고유 공급사 목록
                      const suppliers = Array.from(new Set(list.map(e => e.company)));

                      return suppliers.map((supplier, supIdx) => {
                        // 이 공급사에 속한 모든 견적
                        const bySupplier = list.filter(e => e.company === supplier);
                        // 공급사 내 프로젝트명 순서대로 고유 추출
                        const projectNames = Array.from(new Set(bySupplier.map(e => e.projectName)));

                        return (
                          <React.Fragment key={supplier}>

                            {/* 공급사 그룹 간 구분선 */}
                            {supIdx > 0 && <Divider sx={{ ml: 4, mr: 4, my: 1 }} />}

                            {/* 공급사 헤더 */}
                            <ListItem sx={{ pl: 4, py: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {supplier} 공급사 견적서
                              </Typography>
                            </ListItem>

                            {/* 프로젝트별 그룹 (구분선 없이) */}
                            {projectNames.map(projectName => {
                              const byProject = bySupplier.filter(e => e.projectName === projectName);
                              return (
                                <React.Fragment key={projectName}>
                                  {/* 프로젝트명 헤더 */}
                                  <ListItem sx={{ pl: 6, py: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {projectName}
                                    </Typography>
                                  </ListItem>

                                  {/* 해당 프로젝트의 estimate */}
                                  {byProject.map(est => (
                                    <ListItem
                                      key={est.id}
                                      sx={{ pl: 8, py: 0, bgcolor: 'background.default' }}
                                    >
                                      <ListItemButton onClick={() => handleEstimateClick(est)}>
                                        <ListItemText
                                          primary={
                                            <span style={{ fontWeight: 'bold' }}>
                                              {`${est.estimateNumber} (${est.projectName})`}
                                            </span>
                                          }
                                          secondary={
                                            <>
                                              작성자: {est.createdByNick} | 담당자: {est.contactPerson} | 업로드일:{' '}
                                              {format(new Date(est.uploadDate), 'yyyy-MM-dd HH:mm')}
                                            </>
                                          }
                                        />
                                      </ListItemButton>
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          edge="end"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEstimateDelete(est.id);
                                          }}
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </List>
                </Collapse>
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>
    </Box>

    {/* 드래그 가능한 모달창 (A4 용지 크기) */}
    <DraggableDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      estimate={selectedEstimate}
      onUpdate={handleEstimateSave}
    />

    {/* 견적서 작성 모달 */}
    <EstimateWriteDialog
      open={writeDialogOpen}
      onClose={handleWriteCloseFromList}
      estimate={null} // 신규작성이므로 null
    />
    </div>
  );
};

export default EstimatePage;